import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  IconName,
} from '@hills-design-system/components';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';
import { ModalProps, ModalSize } from './Modal.types';
import { WarningIcon } from '../icons';

/**
 * Modal component for displaying content in an overlay
 */
const Modal: React.FC<ModalProps> = ({
  title,
  eyebrow,
  eyebrowClassName = '',
  secondaryEyebrow,
  children,
  isOpen,
  onClose,
  size = ModalSize.Medium,
  showCloseButton = true,
  className = '',
  subtitle = '',
  errorMessage = '',
  disableClose = false,
  footerContent,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Apply Tailwind classes directly to body element
      const body = document.body;
      const originalStyle = {
        overflow: body.style.overflow,
        paddingRight: body.style.paddingRight,
      };

      // Add Tailwind classes to body
      body.classList.add('overflow-hidden', 'pr-0');

      return () => {
        // Restore original body styles
        body.classList.remove('overflow-hidden', 'pr-0');
        body.style.overflow = originalStyle.overflow;
        body.style.paddingRight = originalStyle.paddingRight;
      };
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !disableClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose, disableClose]);

  // Close when clicking outside the modal content (ignore datepicker clicks)
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const path = e.nativeEvent.composedPath();
    // If click is inside datepicker popper or calendar, do not close
    if (path.some((el) => el instanceof HTMLElement && el.closest('.react-datepicker'))) {
      return;
    }
    if (modalRef.current && !modalRef.current.contains(e.target as Node) && !disableClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Get the modal size class
  const sizeClass = {
    [ModalSize.Small]: 'max-w-lg', // 480px
    [ModalSize.Medium]: 'max-w-2xl', // 640px
    [ModalSize.Large]: 'max-w-4xl', // 800px
  }[size];

  // Portal modal to the end of the document body
  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto no-scrollbar"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className={`relative bg-white rounded-[32px] shadow-md flex flex-col max-h-[90vh] w-full ${sizeClass} ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="flex flex-col w-full">
          <div className="flex justify-end px-8 pt-8">
            {showCloseButton && !disableClose && (
              <div className="flex items-center justify-center">
                <IconButton
                  icon={IconName.X}
                  variant={IconButtonVariant.Secondary}
                  size={IconButtonSize.Medium}
                  onClick={onClose}
                  ariaLabel={t(TranslationKeyEnum.CloseModal)}
                />
              </div>
            )}
          </div>

          {errorMessage && (
            <div className="flex items-center px-8 mb-4">
              <span className="flex items-center gap-3 text-sm text-destructive-dark font-bold">
                <WarningIcon className={`w-6 h-6`} />
                {errorMessage}
              </span>
            </div>
          )}

          {(eyebrow || secondaryEyebrow) && (
            <div className="flex items-center gap-1.5 px-8 mb-2">
              {eyebrow && (
                <span
                  className={
                    eyebrowClassName ??
                    'font-montserrat font-bold text-sm leading-[1.375em] text-[#20252E]'
                  }
                >
                  {eyebrow}
                </span>
              )}
              {eyebrow && secondaryEyebrow && (
                <span className="font-montserrat font-medium text-sm leading-[1.375em] text-black/20">
                  •
                </span>
              )}
              {secondaryEyebrow && (
                <span className="font-montserrat font-medium text-sm leading-[1.375em] text-black">
                  {secondaryEyebrow}
                </span>
              )}
            </div>
          )}

          <h2
            id="modal-title"
            className="font-montserrat font-bold text-[32px] leading-[1.125em] tracking-tight text-[#20252E] px-8 m-0"
          >
            {title}
          </h2>
          {subtitle && <p className="font-normal text-base text-black-600 px-8 m-0">{subtitle}</p>}
        </div>

        <div className="p-8 flex flex-col flex-1 overflow-y-auto no-scrollbar relative">
          {children}
          {footerContent && (
            <div className="sticky bottom-0 right-0 mt-4 flex justify-end">{footerContent}</div>
          )}
        </div>
      </div>
    </div>
  );

  // Portal into the app root so Tailwind's important selector (#root-food-shelter-love) applies
  const portalTarget = document.getElementById('root-food-shelter-love') || document.body;
  return createPortal(modalContent, portalTarget);
};

export default Modal;
